import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const QualityPolicyContainer = styled.div`
  padding: 140px 0 60px;
  
  @media (max-width: 768px) {
    padding: 100px 0 40px;
  }
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #0a1628, #1a2744);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 120px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  height: 500px;
  border-radius: 25px;
  overflow: hidden;
  margin-bottom: 120px;
  
  &.animate-in {
    animation: ${scaleIn} 0.8s ease-out;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80') no-repeat center center;
    background-size: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(10, 22, 40, 0.85), rgba(26, 39, 68, 0.7));
  }
  
  &:hover::before {
    transform: scale(1.05);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
  color: white;
  
  h2 {
    font-size: 3.5rem;
    
    margin-bottom: 25px;
    letter-spacing: -1.5px;
  
    color: #ffffff;}
  
  p {
    font-size: 1.3rem;
    
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
    opacity: 0.95;
  
    color: #ffffff;}
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

// Quality Standards
const StandardsSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .standards-grid .standard-card:nth-child(1) { animation: ${scaleIn} 0.6s ease-out 0.1s both; }
    .standards-grid .standard-card:nth-child(2) { animation: ${scaleIn} 0.6s ease-out 0.3s both; }
    .standards-grid .standard-card:nth-child(3) { animation: ${scaleIn} 0.6s ease-out 0.5s both; }
    .standards-grid .standard-card:nth-child(4) { animation: ${scaleIn} 0.6s ease-out 0.7s both; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  
  color: #2c2c2c;
  margin-bottom: 80px;
  text-align: center;
  letter-spacing: -1.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const StandardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 50px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const StandardCard = styled.div`
  background: white;
  padding: 50px 40px;
  border-radius: 20px;
  border: 1px solid rgba(10, 22, 40, 0.08);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #0a1628, #1a2744);
    transform: scaleY(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(10, 22, 40, 0.12);
    border-color: rgba(10, 22, 40, 0.15);
  }
  
  &:hover::before {
    transform: scaleY(1);
  }
  
  .icon {
    font-size: 3.5rem;
    color: #0a1628;
    margin-bottom: 25px;
    transition: all 0.4s ease;
  }
  
  &:hover .icon {
    transform: scale(1.1);
    color: #1a2744;
  }
  
  h3 {
    font-size: 1.8rem;
    color: #2c2c2c;
    margin-bottom: 15px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #6c757d;
    line-height: 1.7;
    font-size: 1.1rem;
    
    margin-bottom: 20px;
  }
  
  .features {
    list-style: none;
    padding: 0;
    
    li {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
      color: #6c757d;
      font-size: 1rem;
      
      &::before {
        content: '✓';
        color: #0a1628;
        font-weight: bold;
      }
    }
  }
`;

// Process Section
const ProcessSection = styled.section`
  background: linear-gradient(135f, #f8f9fa 0%, #ffffff 100%);
  padding: 100px 0;
  border-radius: 30px;
  margin-bottom: 120px;
  
  &.animate-in {
    .process-steps .process-step:nth-child(1) { animation: ${slideInLeft} 0.6s ease-out 0.1s both; }
    .process-steps .process-step:nth-child(2) { animation: ${slideInLeft} 0.6s ease-out 0.3s both; }
    .process-steps .process-step:nth-child(3) { animation: ${slideInLeft} 0.6s ease-out 0.5s both; }
    .process-steps .process-step:nth-child(4) { animation: ${slideInLeft} 0.6s ease-out 0.7s both; }
    .process-steps .process-step:nth-child(5) { animation: ${slideInLeft} 0.6s ease-out 0.9s both; }
  }
`;

const ProcessSteps = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const ProcessStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 30px;
  margin-bottom: 40px;
  padding: 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  
  &:hover {
    transform: translateX(10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
  
  .step-number {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #0a1628, #1a2744);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 600;
    color: white;
    flex-shrink: 0;
  }
  
  .step-content {
    h3 {
      font-size: 1.6rem;
      color: #2c2c2c;
      margin-bottom: 10px;
      font-weight: 500;
    }
    
    p {
      color: #6c757d;
      line-height: 1.7;
      
    }
  }
`;

// Certifications Section
const CertificationsSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .cert-grid .cert-card:nth-child(1) { animation: ${scaleIn} 0.6s ease-out 0.1s both; }
    .cert-grid .cert-card:nth-child(2) { animation: ${scaleIn} 0.6s ease-out 0.3s both; }
    .cert-grid .cert-card:nth-child(3) { animation: ${scaleIn} 0.6s ease-out 0.5s both; }
  }
`;

const CertGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 50px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CertCard = styled.div`
  background: white;
  padding: 40px 30px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(10, 22, 40, 0.05);
  transition: all 0.4s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.12);
    border-color: rgba(10, 22, 40, 0.15);
  }
  
  .cert-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #0a1628, #1a2744);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: white;
    margin: 0 auto 25px;
    transition: all 0.4s ease;
  }
  
  &:hover .cert-icon {
    transform: scale(1.1);
  }
  
  h3 {
    font-size: 1.5rem;
    color: #2c2c2c;
    margin-bottom: 15px;
    font-weight: 500;
  }
  
  p {
    color: #6c757d;
    line-height: 1.6;
    
  }
`;

const QualityPolicy = () => {
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <QualityPolicyContainer>
      <Container>
        <PageTitle>{t('qualityPolicy.title')}</PageTitle>
        <PageSubtitle>
          {t('qualityPolicy.subtitle')}
        </PageSubtitle>

        <HeroSection 
          id="hero-section" 
          data-animate
          className={animatedSections.has('hero-section') ? 'animate-in' : ''}
        >
          <HeroContent>
            <h2>{t('qualityPolicy.heroTitle')}</h2>
            <p>
              {t('qualityPolicy.heroDesc')}
            </p>
          </HeroContent>
        </HeroSection>

        <StandardsSection 
          id="standards-section" 
          data-animate
          className={animatedSections.has('standards-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('qualityPolicy.standardsTitle')}</SectionTitle>
          <StandardsGrid className="standards-grid">
            <StandardCard className="standard-card">
              <div className="icon">🔬</div>
              <h3>{t('qualityPolicy.std1Title')}</h3>
              <p>{t('qualityPolicy.std1Desc')}</p>
              <ul className="features">
                <li>{t('qualityPolicy.std1f1')}</li>
                <li>{t('qualityPolicy.std1f2')}</li>
                <li>{t('qualityPolicy.std1f3')}</li>
                <li>{t('qualityPolicy.std1f4')}</li>
              </ul>
            </StandardCard>

            <StandardCard className="standard-card">
              <div className="icon">📊</div>
              <h3>{t('qualityPolicy.std2Title')}</h3>
              <p>{t('qualityPolicy.std2Desc')}</p>
              <ul className="features">
                <li>{t('qualityPolicy.std2f1')}</li>
                <li>{t('qualityPolicy.std2f2')}</li>
                <li>{t('qualityPolicy.std2f3')}</li>
                <li>{t('qualityPolicy.std2f4')}</li>
              </ul>
            </StandardCard>

            <StandardCard className="standard-card">
              <div className="icon">🎯</div>
              <h3>{t('qualityPolicy.std3Title')}</h3>
              <p>{t('qualityPolicy.std3Desc')}</p>
              <ul className="features">
                <li>{t('qualityPolicy.std3f1')}</li>
                <li>{t('qualityPolicy.std3f2')}</li>
                <li>{t('qualityPolicy.std3f3')}</li>
                <li>{t('qualityPolicy.std3f4')}</li>
              </ul>
            </StandardCard>

            <StandardCard className="standard-card">
              <div className="icon">🏆</div>
              <h3>{t('qualityPolicy.std4Title')}</h3>
              <p>{t('qualityPolicy.std4Desc')}</p>
              <ul className="features">
                <li>{t('qualityPolicy.std4f1')}</li>
                <li>{t('qualityPolicy.std4f2')}</li>
                <li>{t('qualityPolicy.std4f3')}</li>
                <li>{t('qualityPolicy.std4f4')}</li>
              </ul>
            </StandardCard>
          </StandardsGrid>
        </StandardsSection>

        <ProcessSection 
          id="process-section" 
          data-animate
          className={animatedSections.has('process-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('qualityPolicy.processTitle')}</SectionTitle>
          <ProcessSteps className="process-steps">
            <ProcessStep className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>{t('qualityPolicy.proc1Title')}</h3>
                <p>{t('qualityPolicy.proc1Desc')}</p>
              </div>
            </ProcessStep>

            <ProcessStep className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>{t('qualityPolicy.proc2Title')}</h3>
                <p>{t('qualityPolicy.proc2Desc')}</p>
              </div>
            </ProcessStep>

            <ProcessStep className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>{t('qualityPolicy.proc3Title')}</h3>
                <p>{t('qualityPolicy.proc3Desc')}</p>
              </div>
            </ProcessStep>

            <ProcessStep className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>{t('qualityPolicy.proc4Title')}</h3>
                <p>{t('qualityPolicy.proc4Desc')}</p>
              </div>
            </ProcessStep>

            <ProcessStep className="process-step">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3>{t('qualityPolicy.proc5Title')}</h3>
                <p>{t('qualityPolicy.proc5Desc')}</p>
              </div>
            </ProcessStep>
          </ProcessSteps>
        </ProcessSection>

        <CertificationsSection 
          id="cert-section" 
          data-animate
          className={animatedSections.has('cert-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('qualityPolicy.certTitle')}</SectionTitle>
          
          <div style={{ textAlign: "center", padding: "80px 20px", background: "#f8fafc", borderRadius: "20px", border: "1px dashed #cbd5e1", maxWidth: "800px", margin: "0 auto" }}>
            <div style={{ fontSize: "3rem", marginBottom: "20px" }}>⏳</div>
            <h3 style={{ fontSize: "1.8rem", color: "#1e293b", marginBottom: "15px", fontWeight: "500" }}>{t('technicalDocumentation.comingSoonTitle', 'Çok Yakında!')}</h3>
            <p style={{ fontSize: "1.1rem", color: "#64748b" }}>{t('technicalDocumentation.comingSoonDesc', 'Belgelerimiz ve sertifikalarımız çok yakında eklenecektir.')}</p>
          </div>

        </CertificationsSection>
      </Container>
    </QualityPolicyContainer>
  );
};

export default QualityPolicy;